import type { FC, ReactNode } from 'react'

import Link from 'next/link'
import { ArrowRight } from 'lucide-react'
import { cn } from '@/lib/utils'
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion'
import { useLocale } from '@/i18n/use-locale'
import { common } from '@/i18n/common'

type ActionVariant = 'primary' | 'brand' | 'outline' | 'light' | 'ghost-light'

interface ActionLinkProps {
    href: string
    children: ReactNode
    variant?: ActionVariant
    className?: string
    arrow?: boolean
}

const actionStyles: Record<ActionVariant, string> = {
    'primary': 'bg-ink text-white hover:bg-ink-soft',
    'brand': 'bg-primary text-primary-foreground hover:bg-primary/90',
    'outline': 'border border-border bg-background text-foreground hover:border-foreground/25 hover:bg-mist',
    'light': 'bg-white text-ink hover:bg-white/90',
    'ghost-light': 'border border-white/20 text-white hover:border-white/40 hover:bg-white/5'
}

export const ActionLink: FC<ActionLinkProps> = ({ href, children, variant = 'primary', className, arrow = false }): ReactNode => {
    return (
        <Link
            href={href}
            className={cn(
                'group inline-flex h-12 items-center justify-center gap-2 rounded-full px-6 text-[0.95rem] font-medium transition-colors focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring',
                actionStyles[variant],
                className
            )}
        >
            {children}
            {arrow && <ArrowRight className='size-4 transition-transform group-hover:translate-x-0.5' aria-hidden='true' />}
        </Link>
    )
}

interface EyebrowProps {
    children: ReactNode
    tone?: 'light' | 'dark'
    className?: string
}

export const Eyebrow: FC<EyebrowProps> = ({ children, tone = 'light', className }): ReactNode => {
    return (
        <p className={cn('inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.2em]', tone === 'dark' ? 'text-signal' : 'text-primary', className)}>
            <span className={cn('h-px w-6', tone === 'dark' ? 'bg-signal/60' : 'bg-primary/50')} aria-hidden='true' />
            {children}
        </p>
    )
}

interface SectionHeaderProps {
    eyebrow?: string
    title: ReactNode
    text?: ReactNode
    align?: 'left' | 'center'
    tone?: 'light' | 'dark'
    className?: string
    as?: 'h1' | 'h2'
}

export const SectionHeader: FC<SectionHeaderProps> = ({ eyebrow, title, text, align = 'left', tone = 'light', className, as = 'h2' }): ReactNode => {
    const Heading = as

    return (
        <div className={cn('max-w-3xl', align === 'center' && 'mx-auto text-center', className)}>
            {eyebrow && (
                <Eyebrow tone={tone} className={align === 'center' ? 'justify-center' : undefined}>
                    {eyebrow}
                </Eyebrow>
            )}

            <Heading className={cn('mt-4 font-display text-[2.4rem] leading-[1.05] tracking-[-0.01em] sm:text-5xl', tone === 'dark' ? 'text-white' : 'text-foreground')}>
                {title}
            </Heading>

            {text && (
                <p className={cn('mt-5 text-lg leading-relaxed', tone === 'dark' ? 'text-white/70' : 'text-muted-foreground')}>
                    {text}
                </p>
            )}
        </div>
    )
}

interface PageHeroProps {
    eyebrow?: string
    title: ReactNode
    text?: ReactNode
    children?: ReactNode
    aside?: ReactNode
    tone?: 'light' | 'dark'
}

export const PageHero: FC<PageHeroProps> = ({ eyebrow, title, text, children, aside, tone = 'light' }): ReactNode => {
    const dark = tone === 'dark'

    return (
        <section id='hero' className={cn('relative overflow-hidden pb-20 pt-36 sm:pt-40', dark ? 'bg-ink text-white' : 'bg-mist')}>
            <div className={cn('pointer-events-none absolute inset-0 mask-fade-b', dark ? 'bg-grid-dark opacity-50' : 'bg-grid opacity-60')} aria-hidden='true' />
            <div className='pointer-events-none absolute -right-40 -top-40 size-[520px] rounded-full bg-primary/20 blur-3xl' aria-hidden='true' />

            <div className={cn('container-page relative grid items-end gap-12', aside && 'lg:grid-cols-[1.4fr_1fr]')}>
                <div>
                    {eyebrow && <Eyebrow tone={dark ? 'dark' : 'light'}>{eyebrow}</Eyebrow>}

                    <h1 className={cn('mt-5 max-w-4xl font-display text-[2.9rem] leading-[1.02] tracking-[-0.015em] sm:text-6xl lg:text-[4.4rem]', dark ? 'text-white' : 'text-foreground')}>
                        {title}
                    </h1>

                    {text && (
                        <p className={cn('mt-6 max-w-2xl text-lg leading-relaxed sm:text-xl', dark ? 'text-white/70' : 'text-muted-foreground')}>
                            {text}
                        </p>
                    )}

                    {children && <div className='mt-9 flex flex-wrap gap-3'>{children}</div>}
                </div>

                {aside && <div>{aside}</div>}
            </div>
        </section>
    )
}

interface CtaBandProps {
    title?: string
    text?: string
}

export const CtaBand: FC<CtaBandProps> = ({ title, text }): ReactNode => {
    const locale = useLocale()
    const copy = common[locale].cta

    return (
        <section id='consultation' className='bg-background py-24'>
            <div className='container-page'>
                <div className='relative overflow-hidden rounded-[2rem] bg-ink px-8 py-14 text-white sm:px-14 sm:py-16'>
                    <div className='bg-grid-dark mask-radial pointer-events-none absolute inset-0 opacity-60' aria-hidden='true' />
                    <div className='pointer-events-none absolute -bottom-32 -left-20 size-96 rounded-full bg-primary/40 blur-3xl' aria-hidden='true' />
                    <div className='pointer-events-none absolute -right-24 -top-24 size-72 rounded-full bg-signal/20 blur-3xl' aria-hidden='true' />

                    <div className='relative flex flex-col gap-10 lg:flex-row lg:items-end lg:justify-between'>
                        <div className='max-w-2xl'>
                            <h2 className='font-display text-4xl leading-[1.05] sm:text-5xl'>
                                {title ?? copy.title}
                            </h2>

                            <p className='mt-5 text-lg leading-relaxed text-white/70'>
                                {text ?? copy.text}
                            </p>
                        </div>

                        <div className='flex flex-wrap gap-3'>
                            <ActionLink href='/contact' variant='light' arrow>
                                {copy.primary}
                            </ActionLink>

                            <ActionLink href='/pricing' variant='ghost-light'>
                                {copy.secondary}
                            </ActionLink>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    )
}

interface FaqListProps {
    items: Array<{ question: string, answer: string }>
}

export const FaqList: FC<FaqListProps> = ({ items }): ReactNode => {
    return (
        <Accordion className='divide-y divide-border border-y border-border'>
            {items.map((item, index) => (
                <AccordionItem key={item.question} value={`faq-${index}`} className='border-0'>
                    <AccordionTrigger className='py-5 text-left text-base font-semibold hover:no-underline'>
                        {item.question}
                    </AccordionTrigger>

                    <AccordionContent className='pb-5 text-[0.98rem] leading-relaxed text-muted-foreground'>
                        {item.answer}
                    </AccordionContent>
                </AccordionItem>
            ))}
        </Accordion>
    )
}

export const faqJsonLd = (items: Array<{ question: string, answer: string }>): Record<string, unknown> => ({
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: items.map(item => ({
        '@type': 'Question',
        name: item.question,
        acceptedAnswer: { '@type': 'Answer', text: item.answer }
    }))
})
